// The proven Privy bootstrap from the feasibility spike (KTD2), shared by
// every page that talks to the provisioned wallet: fetch the SDK bundle only
// when asked (KTD1), construct the client, mount the hidden key-holder iframe
// and bridge postMessage both ways.
//
// Tests can't drive the hosted iframe, so a page-injected
// `window.privyTestSdk` replaces the SDK module wholesale (see
// test/system/privy_onboarding_test.rb); with it no iframe is mounted.
export async function createPrivyClient({ sdkUrl, appId, container }) {
  if (!appId) throw new Error("missing Privy app id")

  const sdk = window.privyTestSdk || await import(sdkUrl)
  const privy = new sdk.Privy({ appId, storage: new sdk.LocalStorage() })
  let teardown = () => {}

  if (!window.privyTestSdk) {
    // Embedded-wallet keys live in a Privy-hosted iframe. Mount it and bridge
    // postMessage both ways: SDK -> iframe via setMessagePoster, iframe -> SDK
    // via embeddedWallet.onMessage.
    const iframe = document.createElement("iframe")
    iframe.src = privy.embeddedWallet.getURL()
    iframe.style.display = "none"
    iframe.setAttribute("aria-hidden", "true")
    container.appendChild(iframe)
    const iframeOrigin = new URL(iframe.src).origin

    privy.setMessagePoster({
      postMessage: (message, targetOrigin, transfer) =>
        iframe.contentWindow.postMessage(message, targetOrigin, transfer ? [transfer] : undefined),
      reload: () => { iframe.src = iframe.src }
    })

    const onWindowMessage = (event) => {
      if (event.origin !== iframeOrigin) return
      try { privy.embeddedWallet.onMessage(event.data) } catch { /* not a message for the SDK */ }
    }
    window.addEventListener("message", onWindowMessage)
    teardown = () => {
      window.removeEventListener("message", onWindowMessage)
      iframe.remove()
    }
  }

  await privy.initialize()
  return { sdk, privy, teardown }
}
